/**
 * SecurityOverviewTab Component
 *
 * Unified security posture summary providing:
 * - Guardian Security Score (4-component: 30% Microsoft Secure Score, 30% BPA, 20% MFA, 20% Config)
 * - SCuBA Deep Scan standalone card
 * - "Needs Attention" section with top issues across BPA + Secure Score, severity-first
 * - Score trend comparison (vs last week)
 * - Multi-tenant comparison for MSPs
 * - Framework recommendations based on coverage
 * - Cross-navigation to Command Center
 *
 * Part of the Security Center hub - Phase 3 implementation (Dashboard Consolidation).
 *
 * @example
 * ```tsx
 * const overviewRef = useRef<SecurityOverviewTabRef>(null)
 *
 * <SecurityOverviewTab
 *   ref={overviewRef}
 *   tenantId={selectedTenantId}
 *   onNavigateToTab={(tab) => setActiveTab(tab)}
 * />
 *
 * // Refresh all data from parent
 * overviewRef.current?.refresh()
 * ```
 */

import { forwardRef, useImperativeHandle, useMemo, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api'
import toast from 'react-hot-toast'
import {
  ShieldCheckIcon,
  ClipboardDocumentCheckIcon,
  DocumentMagnifyingGlassIcon,
  DocumentCheckIcon,
  ExclamationTriangleIcon,
  ChevronRightIcon,
  CheckCircleIcon,
  InformationCircleIcon,
  KeyIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  MinusIcon,
  ArrowTopRightOnSquareIcon,
  Squares2X2Icon,
} from '@heroicons/react/24/outline'
import {
  useComplianceDashboard,
  useComplianceStandards,
  useScoreTrend,
  calculateStarredBpaScore,
  getIncludedFrameworkNames,
} from '@/hooks/useComplianceData'
import { useTenantContext } from '@/hooks/useTenantContext'
import type {
  SecureScoreSummary,
  ScubaSummary,
  SecureScoreControlProfile,
} from '@/types'
import ComplianceRadialScore from '@/components/Compliance/ComplianceRadialScore'
import MultiTenantScoreComparison from './MultiTenantScoreComparison'
import FrameworkRecommendation from './FrameworkRecommendation'

export interface SecurityOverviewTabRef {
  /** Refresh all data sources */
  refresh: () => void
}

export interface SecurityOverviewTabProps {
  /** The tenant ID to display overview for */
  tenantId: string | undefined
  /** Callback to navigate to another tab */
  onNavigateToTab?: (tabKey: 'best-practices' | 'secure-score' | 'scuba') => void
}

/** Severity order for sorting issues */
const SEVERITY_ORDER: Record<string, number> = {
  'SHALL': 0,    // Critical (BPA)
  'Critical': 0, // Critical (Secure Score)
  'SHOULD': 1,   // High (BPA)
  'High': 1,     // High (Secure Score)
  'MAY': 2,      // Medium (BPA)
  'Medium': 2,   // Medium (Secure Score)
  'Low': 3,      // Low
}

/** Unified issue type for aggregation */
interface UnifiedIssue {
  id: string
  title: string
  source: 'bpa' | 'secure-score'
  severity: string
  severityOrder: number
  category?: string
  product?: string
  /**
   * Microsoft Secure Score points to gain by implementing this control.
   * Only set on `secure-score` items, where Microsoft publishes per-control
   * `maxScore` / `achievedScore`. Not synthesized for BPA items.
   */
  secureScorePointsToGain?: number
  /** Real framework citations (e.g. "CIS 1.1.1", "NIST AC-7") for BPA items. */
  frameworkReferences?: string[]
  azurePortalUrl?: string  // Deep link for remediation
}

/**
 * SecurityOverviewTab - Unified security posture dashboard
 *
 * Guardian Score Algorithm (30/30/20/20):
 * - Microsoft Secure Score: 30% weight (baseline security)
 * - Best Practice Analyzer: 30% weight (STARRED frameworks only + MCSB)
 * - MFA Adoption: 20% weight (identity hygiene)
 * - Configuration Compliance: 20% weight (drift status)
 *
 * Weights normalize based on available data sources.
 */
const SecurityOverviewTab = forwardRef<
  SecurityOverviewTabRef,
  SecurityOverviewTabProps
>(function SecurityOverviewTab({ tenantId, onNavigateToTab }, ref) {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { isGlobalAdmin } = useTenantContext()

  // BPA data from compliance dashboard hook
  const {
    dashboardData: bpaSummary,
    violations: bpaViolations,
    isLoading: bpaLoading,
    refetch: refetchBpa,
  } = useComplianceDashboard(tenantId)

  // Secure Score data
  const {
    data: secureScoreSummary,
    isLoading: secureScoreLoading,
    refetch: refetchSecureScore,
  } = useQuery<SecureScoreSummary | null>({
    queryKey: ['secureScoreSummary', tenantId],
    queryFn: () => api.getSecureScoreSummary(tenantId!),
    enabled: !!tenantId,
    staleTime: 5 * 60 * 1000,
  })

  // SCuBA summary data
  const {
    data: scubaSummary,
    isLoading: scubaLoading,
    refetch: refetchScuba,
  } = useQuery<ScubaSummary>({
    queryKey: ['scuba-summary', tenantId],
    queryFn: () => api.getScubaSummary(tenantId!),
    enabled: !!tenantId,
  })

  // User Insights data (for MFA adoption score)
  const {
    data: userInsights,
    isLoading: userInsightsLoading,
    refetch: refetchUserInsights,
  } = useQuery({
    queryKey: ['user-insights', tenantId],
    queryFn: () => api.getUserInsights(tenantId!),
    enabled: !!tenantId,
    staleTime: 5 * 60 * 1000,
  })

  // Dashboard stats data (for config compliance score)
  const {
    data: dashboardStats,
    isLoading: dashboardStatsLoading,
    refetch: refetchDashboard,
  } = useQuery({
    queryKey: ['dashboard', tenantId],
    queryFn: () => api.getDashboardStats(tenantId!),
    enabled: !!tenantId,
    staleTime: 5 * 60 * 1000,
  })

  // All compliance standards (for framework recommendations)
  const { data: allStandards } = useComplianceStandards()

  // Secure Score refresh mutation
  const refreshSecureScoreMutation = useMutation({
    mutationFn: () => api.refreshSecureScore(tenantId!),
    onSuccess: (data) => {
      if (data) {
        queryClient.setQueryData(['secureScoreSummary', tenantId], data)
        toast.success('Secure Score refreshed successfully')
      }
    },
    onError: () => {
      toast.error('Failed to refresh Secure Score')
    },
  })

  // SCuBA scan mutation
  const startScubaScanMutation = useMutation({
    mutationFn: () => api.startScubaScan(tenantId!),
    onSuccess: () => {
      toast.success('SCuBA scan started! This may take several minutes.')
      // Navigate to SCuBA tab to show progress
      onNavigateToTab?.('scuba')
    },
    onError: (error: Error) => {
      toast.error(`Failed to start SCuBA scan: ${error.message || 'Unknown error'}`)
    },
  })

  // Refresh all data sources
  const handleRefreshAll = useCallback(() => {
    refetchBpa()
    refetchSecureScore()
    refetchScuba()
    refetchUserInsights()
    refetchDashboard()
    toast.success('Refreshing all security data...')
  }, [refetchBpa, refetchSecureScore, refetchScuba, refetchUserInsights, refetchDashboard])

  // Expose refresh method to parent via ref
  useImperativeHandle(
    ref,
    () => ({
      refresh: handleRefreshAll,
    }),
    [handleRefreshAll]
  )

  // Calculate BPA score from starred frameworks only (MCSB always included)
  const bpaStarredScore = useMemo(() => {
    return calculateStarredBpaScore(bpaSummary?.frameworks)
  }, [bpaSummary?.frameworks])

  // Get list of framework names included in the Guardian Score (for transparency)
  const includedFrameworkNames = useMemo(() => {
    return getIncludedFrameworkNames(bpaSummary?.frameworks)
  }, [bpaSummary?.frameworks])

  // Check if any framework has been explicitly selected for Guardian Score inclusion.
  // MCSB is auto-included in score calculation regardless, but this flag tracks
  // whether the user has consciously opted in any framework.
  const hasExplicitlySelectedFrameworks = useMemo(() => {
    if (!bpaSummary?.frameworks || bpaSummary.frameworks.length === 0) return true
    return bpaSummary.frameworks.some(f => f.includeInGuardianScore)
  }, [bpaSummary?.frameworks])

  // Calculate composite Guardian Security Score (NEW 30/30/20/20 algorithm)
  const guardianScore = useMemo(() => {
    const scores: { value: number; weight: number; source: string; icon: string }[] = []

    // 1. Microsoft Secure Score (30% weight)
    if (secureScoreSummary?.score?.currentScore != null && secureScoreSummary?.score?.maxScore) {
      const pct = (secureScoreSummary.score.currentScore / secureScoreSummary.score.maxScore) * 100
      scores.push({
        value: pct,
        weight: 30,
        source: 'Microsoft Secure Score',
        icon: 'ClipboardDocumentCheckIcon'
      })
    }

    // 2. BPA Score from starred frameworks (30% weight)
    if (bpaStarredScore != null) {
      scores.push({
        value: bpaStarredScore,
        weight: 30,
        source: 'Best Practices',
        icon: 'ShieldCheckIcon'
      })
    }

    // 3. MFA Adoption (20% weight)
    const mfaScore = userInsights?.mfaMemberAdoptionPercentage
    if (mfaScore != null) {
      scores.push({
        value: mfaScore,
        weight: 20,
        source: 'MFA Adoption',
        icon: 'KeyIcon'
      })
    }

    // 4. Configuration Compliance (20% weight) - from drift detection
    const configScore = dashboardStats?.complianceScore?.percentage
    if (configScore != null) {
      scores.push({
        value: configScore,
        weight: 20,
        source: 'Config Compliance',
        icon: 'DocumentCheckIcon'
      })
    }

    if (scores.length === 0) return null

    // Normalize weights to 100%
    const totalWeight = scores.reduce((sum, s) => sum + s.weight, 0)
    const weightedSum = scores.reduce((sum, s) => sum + (s.value * s.weight), 0)

    return {
      score: Math.round(weightedSum / totalWeight),
      sources: scores,
      totalWeight,
      includedFrameworks: includedFrameworkNames,
    }
  }, [secureScoreSummary, bpaStarredScore, userInsights, dashboardStats, includedFrameworkNames])

  // Score trend comparison (vs last week)
  const { trend: scoreTrend } = useScoreTrend(tenantId, guardianScore?.score, 7)

  // Aggregate top issues across BPA + Secure Score, ranked on real metrics only.
  //
  // Ranking inputs (no synthesized point model):
  //   - BPA: filtered to open SHALL violations from frameworks the user opted into
  //     the Guardian Score. We surface real `frameworkReferences` and severity; we
  //     do NOT compute a per-violation "Guardian Score impact" — that model doesn't
  //     exist in the data layer (see posture-pages-supplements.md #8).
  //   - Secure Score: Microsoft publishes a real per-control `maxScore` and
  //     `achievedScore`. The delta is the actual Secure Score points the tenant
  //     would gain by implementing the control. We surface that as-is.
  //
  // Sort order: severity tier first (SHALL/Critical > SHOULD/High > MAY/Medium > Low),
  // then title for stability. We balance slots across both sources so neither
  // dominates the list when one has many items.
  const needsAttention = useMemo((): UnifiedIssue[] => {
    const issues: UnifiedIssue[] = []

    // BPA: filter to opted-in frameworks and SHALL severity. No impact synthesis.
    const includedFrameworkIds = new Set(
      (bpaSummary?.frameworks ?? [])
        .filter(f => f.includeInGuardianScore)
        .map(f => f.standardId),
    )
    const openBpaViolations = bpaViolations.filter(
      v => v.status === 'Open' && v.criticality === 'SHALL' && includedFrameworkIds.has(v.standardId),
    )
    for (const violation of openBpaViolations) {
      issues.push({
        id: `bpa-${violation.id}`,
        title: violation.policyTitle || violation.message,
        source: 'bpa',
        severity: violation.criticality,
        severityOrder: SEVERITY_ORDER[violation.criticality] ?? 3,
        category: violation.category,
        product: violation.product,
        frameworkReferences: violation.frameworkReferences,
      })
    }

    // Secure Score: real Microsoft-published `maxScore - achievedScore` per control.
    const ssRecommendations = secureScoreSummary?.recommendations?.filter(
      (r: SecureScoreControlProfile) => !r.isImplemented,
    ) || []
    for (const rec of ssRecommendations) {
      const pointsToGain = Math.max(0, (rec.maxScore ?? 0) - (rec.achievedScore ?? 0))

      issues.push({
        id: `ss-${rec.id}`,
        title: rec.title,
        source: 'secure-score',
        severity: rec.userImpact === 'High' ? 'High' : rec.userImpact === 'Low' ? 'Low' : 'Medium',
        severityOrder: rec.userImpact === 'High' ? 1 : rec.userImpact === 'Low' ? 3 : 2,
        category: rec.controlCategory,
        secureScorePointsToGain: pointsToGain > 0 ? pointsToGain : undefined,
        azurePortalUrl: rec.implementationUrl || rec.actionUrl || undefined,
      })
    }

    // Per-source sort: severity, then Secure Score points (when present), then title.
    const sortBySeverityThenPoints = (a: UnifiedIssue, b: UnifiedIssue) => {
      if (a.severityOrder !== b.severityOrder) return a.severityOrder - b.severityOrder
      const aPts = a.secureScorePointsToGain ?? 0
      const bPts = b.secureScorePointsToGain ?? 0
      if (bPts !== aPts) return bPts - aPts
      return a.title.localeCompare(b.title)
    }

    const ssIssues = issues.filter(i => i.source === 'secure-score').sort(sortBySeverityThenPoints)
    const bpaIssues = issues.filter(i => i.source === 'bpa').sort(sortBySeverityThenPoints)

    const TOTAL_SLOTS = 10
    const HALF = Math.floor(TOTAL_SLOTS / 2)

    // Each source gets up to half the slots; if one has fewer, the other gets the remainder
    const bpaSlotCount = Math.min(
      bpaIssues.length,
      HALF + Math.max(0, HALF - ssIssues.length),
    )
    const ssSlots = Math.min(ssIssues.length, TOTAL_SLOTS - bpaSlotCount)

    const selected = [
      ...bpaIssues.slice(0, bpaSlotCount),
      ...ssIssues.slice(0, ssSlots),
    ]

    // Final sort: severity-first across both sources for a unified critical-first list.
    selected.sort(sortBySeverityThenPoints)

    return selected
  }, [bpaViolations, bpaSummary?.frameworks, secureScoreSummary])

  // Loading state
  const isLoading = bpaLoading || secureScoreLoading || scubaLoading || userInsightsLoading || dashboardStatsLoading
  const hasAnyData = bpaSummary || secureScoreSummary || scubaSummary?.hasReport || userInsights || dashboardStats

  // No tenant selected state
  if (!tenantId) {
    return (
      <div className="bg-dark-800 rounded-2xl border border-dark-700 p-8 text-center">
        <ShieldCheckIcon className="h-16 w-16 text-slate-500 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-slate-100 mb-2">Select a Tenant</h3>
        <p className="text-slate-400">Please select a tenant to view security overview.</p>
      </div>
    )
  }

  // Loading state
  if (isLoading && !hasAnyData) {
    return (
      <div className="space-y-6">
        {/* Guardian Score Skeleton */}
        <div className="bg-dark-800 rounded-2xl border border-dark-700 p-8 animate-pulse">
          <div className="flex flex-col lg:flex-row items-center gap-8">
            <div className="w-48 h-48 bg-dark-700 rounded-full" />
            <div className="flex-1 space-y-4">
              <div className="h-6 bg-dark-700 rounded w-48" />
              <div className="h-4 bg-dark-700 rounded w-64" />
              <div className="grid grid-cols-3 gap-4">
                <div className="h-20 bg-dark-700 rounded-xl" />
                <div className="h-20 bg-dark-700 rounded-xl" />
                <div className="h-20 bg-dark-700 rounded-xl" />
              </div>
            </div>
          </div>
        </div>

        {/* SCuBA Card Skeleton */}
        <div className="bg-dark-800 rounded-2xl border border-dark-700 p-6 h-24 animate-pulse" />
      </div>
    )
  }

  // Get score color class
  const getScoreColorClass = (score: number | null | undefined): string => {
    if (score == null) return 'text-slate-400'
    if (score >= 80) return 'text-emerald-400'
    if (score >= 60) return 'text-amber-400'
    return 'text-red-400'
  }

  // Get severity badge styles
  const getSeverityBadge = (severity: string) => {
    switch (severity) {
      case 'SHALL':
      case 'Critical':
        return 'bg-red-500/15 text-red-400 border-red-500/30'
      case 'SHOULD':
      case 'High':
        return 'bg-orange-500/15 text-orange-400 border-orange-500/30'
      case 'MAY':
      case 'Medium':
        return 'bg-amber-500/15 text-amber-400 border-amber-500/30'
      default:
        return 'bg-slate-500/15 text-slate-400 border-slate-500/30'
    }
  }

  // Get source badge styles
  const getSourceBadge = (source: 'bpa' | 'secure-score') => {
    switch (source) {
      case 'bpa':
        return { bg: 'bg-primary-500/15', text: 'text-primary-400', label: 'BPA' }
      case 'secure-score':
        return { bg: 'bg-blue-500/15', text: 'text-blue-400', label: 'Secure Score' }
    }
  }

  // Helper to render icon for score component
  const renderSourceIcon = (iconName: string) => {
    switch (iconName) {
      case 'ClipboardDocumentCheckIcon':
        return <ClipboardDocumentCheckIcon className="h-4 w-4 text-blue-400" />
      case 'ShieldCheckIcon':
        return <ShieldCheckIcon className="h-4 w-4 text-primary-400" />
      case 'KeyIcon':
        return <KeyIcon className="h-4 w-4 text-amber-400" />
      case 'DocumentCheckIcon':
        return <DocumentCheckIcon className="h-4 w-4 text-emerald-400" />
      default:
        return <ShieldCheckIcon className="h-4 w-4 text-slate-400" />
    }
  }

  return (
    <div className="space-y-8">
      {/* Guardian Security Score */}
      <div className="bg-dark-800 rounded-2xl border border-dark-700 p-8">
        <div className="flex flex-col lg:flex-row items-center gap-8">
          {/* Radial Score */}
          <div className="flex-shrink-0">
            {guardianScore ? (
              <ComplianceRadialScore
                score={guardianScore.score}
                label="Guardian Score"
                size="lg"
              />
            ) : (
              <div className="w-48 h-48 rounded-full bg-dark-700/50 border-2 border-dashed border-dark-600 flex items-center justify-center">
                <div className="text-center">
                  <InformationCircleIcon className="h-12 w-12 text-slate-500 mx-auto mb-2" />
                  <span className="text-sm text-slate-500">No data</span>
                </div>
              </div>
            )}
          </div>

          {/* Score Details */}
          <div className="flex-1 text-center lg:text-left">
            <div className="flex items-center gap-3 justify-center lg:justify-start flex-wrap">
              <h2 className="text-2xl font-bold text-slate-100">Guardian Security Score</h2>

              {/* Trend Badge */}
              {scoreTrend && (
                <div className={`
                  inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium
                  ${scoreTrend.direction === 'up'
                    ? 'bg-emerald-500/15 text-emerald-400 border border-emerald-500/30'
                    : scoreTrend.direction === 'down'
                    ? 'bg-red-500/15 text-red-400 border border-red-500/30'
                    : 'bg-slate-500/15 text-slate-400 border border-slate-500/30'
                  }
                `}>
                  {scoreTrend.direction === 'up' && (
                    <ArrowTrendingUpIcon className="h-3.5 w-3.5" />
                  )}
                  {scoreTrend.direction === 'down' && (
                    <ArrowTrendingDownIcon className="h-3.5 w-3.5" />
                  )}
                  {scoreTrend.direction === 'stable' && (
                    <MinusIcon className="h-3.5 w-3.5" />
                  )}
                  <span>
                    {scoreTrend.direction === 'stable'
                      ? 'No change'
                      : `${scoreTrend.direction === 'up' ? '+' : '-'}${scoreTrend.change}pts`}
                  </span>
                  <span className="text-slate-500">vs last {scoreTrend.period}</span>
                </div>
              )}
            </div>

            <p className="mt-2 text-slate-400">
              {guardianScore
                ? `Composite score from ${guardianScore.sources.length} data source${guardianScore.sources.length !== 1 ? 's' : ''}`
                : 'Run assessments to calculate your security score'}
            </p>

            {/* Score breakdown - 4 component grid */}
            {guardianScore && (
              <div className="mt-6">
                {/* Transparency note showing included frameworks */}
                {guardianScore.includedFrameworks.length > 0 && (
                  <div className="mb-4 text-xs text-slate-500 flex items-center gap-1">
                    <InformationCircleIcon className="h-3.5 w-3.5" />
                    <span>
                      BPA includes: {guardianScore.includedFrameworks.join(', ')}
                    </span>
                  </div>
                )}

                {/* 4-component breakdown grid */}
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                  {guardianScore.sources.map((source) => {
                    const normalizedWeight = Math.round((source.weight / guardianScore.totalWeight) * 100)

                    return (
                      <div
                        key={source.source}
                        className="bg-dark-900/50 rounded-xl p-3 border border-dark-600"
                      >
                        <div className="flex items-center gap-2 mb-2">
                          <div className="p-1.5 rounded-lg bg-dark-700">
                            {renderSourceIcon(source.icon)}
                          </div>
                          <span className="text-xs text-slate-400 truncate">{source.source}</span>
                        </div>
                        <div className={`text-xl font-bold ${getScoreColorClass(source.value)}`}>
                          {Math.round(source.value)}%
                        </div>
                        <div className="text-xs text-slate-500">
                          {normalizedWeight}% weight
                        </div>
                      </div>
                    )
                  })}

                  {/* BPA placeholder when no frameworks are selected for Guardian Score */}
                  {!hasExplicitlySelectedFrameworks && !guardianScore.sources.some(s => s.source === 'Best Practices') && (
                    <div
                      className="bg-dark-900/50 rounded-xl p-3 border border-amber-500/40 cursor-pointer hover:border-amber-500/60 transition-colors"
                      onClick={() => onNavigateToTab?.('best-practices')}
                      role="button"
                      title="Select frameworks to include in your Guardian Score"
                    >
                      <div className="flex items-center gap-2 mb-2">
                        <div className="p-1.5 rounded-lg bg-dark-700">
                          {renderSourceIcon('ShieldCheckIcon')}
                        </div>
                        <span className="text-xs text-slate-400 truncate">Best Practices</span>
                        <ExclamationTriangleIcon className="h-3.5 w-3.5 text-amber-400 flex-shrink-0" />
                      </div>
                      <div className="text-xl font-bold text-slate-500">
                        --
                      </div>
                      <div className="mt-1.5 text-xs text-amber-400">
                        No frameworks selected
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Multi-Tenant Comparison - Only for MSPs with multiple tenants */}
      <MultiTenantScoreComparison />

      {/* Needs Attention Section */}
      <div className="bg-dark-800 rounded-2xl border border-dark-700 overflow-hidden">
        <div className="px-6 py-4 border-b border-dark-700 flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-slate-100 flex items-center gap-2">
              <ExclamationTriangleIcon className="h-5 w-5 text-amber-400" />
              Needs Attention
            </h3>
            <p className="text-sm text-slate-400 mt-1 flex items-center gap-1.5">
              Here are {needsAttention.length} issues you can resolve right now to improve your score
              <span className="relative group">
                <InformationCircleIcon className="h-4 w-4 text-slate-500 hover:text-slate-300 cursor-help transition-colors" />
                <span className="absolute top-full left-1/2 -translate-x-1/2 mt-2 w-80 px-3 py-2.5 rounded-lg bg-dark-700 border border-dark-600 text-xs text-slate-300 leading-relaxed opacity-0 pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto transition-opacity z-50 shadow-lg">
                  Shows up to 10 open issues, severity-first (SHALL / Critical &gt; SHOULD / High &gt; MAY / Medium). Slots are balanced between BPA and Secure Score so both sources are always represented. Secure Score rows show Microsoft-published points-to-gain when available. Visit the Best Practices or Secure Score tabs for the full list.
                </span>
              </span>
            </p>
          </div>
          <span className="px-3 py-1 rounded-full text-sm font-medium bg-amber-500/15 text-amber-400 border border-amber-500/30">
            {needsAttention.length} issue{needsAttention.length !== 1 ? 's' : ''}
          </span>
        </div>

        {needsAttention.length > 0 ? (
          <div className="divide-y divide-dark-700">
            {needsAttention.map((issue) => {
              const sourceBadge = getSourceBadge(issue.source)
              return (
                <div
                  key={issue.id}
                  className="px-6 py-4 hover:bg-dark-700/30 transition-colors cursor-pointer"
                  onClick={() => {
                    // Deep link to Azure Portal if available, otherwise navigate to tab
                    if (issue.azurePortalUrl) {
                      window.open(issue.azurePortalUrl, '_blank')
                    } else if (issue.source === 'bpa' && isGlobalAdmin) {
                      onNavigateToTab?.('best-practices')
                    } else if (issue.source === 'secure-score') {
                      onNavigateToTab?.('secure-score')
                    }
                  }}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium border ${getSeverityBadge(issue.severity)}`}>
                          {issue.severity}
                        </span>
                        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${sourceBadge.bg} ${sourceBadge.text}`}>
                          {sourceBadge.label}
                        </span>
                        {issue.category && (
                          <span className="text-xs text-slate-500">{issue.category}</span>
                        )}
                      </div>
                      <p className="mt-2 text-sm text-slate-200 line-clamp-2">{issue.title}</p>
                    </div>

                    {/* Real-metric badge: Microsoft Secure Score points-to-gain (SS only)
                        or framework-citation count (BPA). No synthesized impact figures. */}
                    <div className="flex items-center gap-3 flex-shrink-0">
                      {issue.source === 'secure-score' && issue.secureScorePointsToGain != null && (
                        <span
                          className="px-2.5 py-1 rounded-lg text-xs font-bold bg-emerald-500/15 text-emerald-400 border border-emerald-500/30"
                          title="Microsoft Secure Score points to gain by implementing this control"
                        >
                          +{issue.secureScorePointsToGain.toFixed(issue.secureScorePointsToGain >= 1 ? 0 : 1)} pts
                        </span>
                      )}
                      {issue.source === 'bpa' && issue.frameworkReferences && issue.frameworkReferences.length > 0 && (
                        <span
                          className="px-2.5 py-1 rounded-lg text-xs font-medium bg-primary-500/10 text-primary-300 border border-primary-500/20"
                          title={issue.frameworkReferences.join(', ')}
                        >
                          {issue.frameworkReferences.length} framework{issue.frameworkReferences.length !== 1 ? 's' : ''}
                        </span>
                      )}
                      {issue.azurePortalUrl ? (
                        <ArrowTopRightOnSquareIcon className="h-5 w-5 text-blue-400" />
                      ) : (
                        <ChevronRightIcon className="h-5 w-5 text-slate-500" />
                      )}
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        ) : (
          <div className="px-6 py-12 text-center">
            <CheckCircleIcon className="h-12 w-12 text-emerald-500 mx-auto mb-4" />
            <h4 className="text-lg font-medium text-slate-200">Looking Good!</h4>
            <p className="mt-2 text-sm text-slate-400">
              {hasAnyData
                ? 'No critical issues found across your security assessments.'
                : 'Run security assessments to see issues that need attention.'}
            </p>
          </div>
        )}
      </div>

      {/* Framework Recommendations - suggests frameworks to improve coverage */}
      {bpaSummary?.frameworks && allStandards && (
        <FrameworkRecommendation
          enabledFrameworks={bpaSummary.frameworks}
          allFrameworks={allStandards.map(s => ({
            code: s.code,
            name: s.name,
            description: s.description || '',
          }))}
          onEnableFramework={() => {
            // Navigate to BPA tab to enable framework
            onNavigateToTab?.('best-practices')
          }}
        />
      )}

      {/* Empty State - When no data at all */}
      {!hasAnyData && !isLoading && (
        <div className="bg-gradient-to-r from-primary-500/10 via-primary-600/5 to-transparent border border-primary-500/20 rounded-2xl p-8 text-center">
          <ShieldCheckIcon className="h-16 w-16 text-primary-400 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-slate-100">Get Started with Security Assessments</h3>
          <p className="mt-3 text-slate-400 max-w-2xl mx-auto">
            Run your first security assessment to establish your Guardian Security Score.
            We recommend starting with Microsoft Secure Score for immediate insights.
          </p>
          <div className="mt-6 flex flex-wrap justify-center gap-4">
            <button
              onClick={() => refreshSecureScoreMutation.mutate()}
              disabled={refreshSecureScoreMutation.isPending}
              className="inline-flex items-center gap-2 px-6 py-3 text-sm font-medium rounded-xl bg-gradient-to-r from-primary-600 to-primary-500 text-white shadow-glow-cyan hover:shadow-glow-cyan-lg hover:-translate-y-0.5 transition-all duration-200 disabled:from-dark-600 disabled:to-dark-600 disabled:shadow-none disabled:cursor-not-allowed"
            >
              <ClipboardDocumentCheckIcon className="h-5 w-5" />
              {refreshSecureScoreMutation.isPending ? 'Fetching...' : 'Fetch Secure Score'}
            </button>
            <button
              onClick={() => startScubaScanMutation.mutate()}
              disabled={startScubaScanMutation.isPending}
              className="inline-flex items-center gap-2 px-6 py-3 text-sm font-medium rounded-xl bg-gradient-to-r from-primary-600 to-primary-500 text-white shadow-glow-cyan hover:shadow-glow-cyan-lg hover:-translate-y-0.5 transition-all duration-200 disabled:from-dark-600 disabled:to-dark-600 disabled:shadow-none disabled:cursor-not-allowed"
            >
              <DocumentMagnifyingGlassIcon className="h-5 w-5" />
              {startScubaScanMutation.isPending ? 'Starting...' : 'Run SCuBA Scan'}
            </button>
          </div>
        </div>
      )}

      {/* Cross-Navigation Card - Link to Command Center */}
      <div className="mt-8 bg-gradient-to-r from-dark-800 to-dark-800/50 rounded-2xl border border-dark-700 p-6">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <h3 className="text-lg font-semibold text-slate-100">
              Need to see operational changes?
            </h3>
            <p className="mt-1 text-sm text-slate-400">
              The Command Center shows drift events, recent activity, and items needing immediate action.
            </p>
          </div>
          <button
            onClick={() => navigate('/')}
            className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-medium rounded-xl bg-primary-500/10 text-primary-400 border border-primary-500/30 hover:bg-primary-500/20 transition-colors whitespace-nowrap"
          >
            <Squares2X2Icon className="h-4 w-4" />
            Go to Command Center
          </button>
        </div>
      </div>
    </div>
  )
})

export default SecurityOverviewTab
